import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import logoImage from '@assets/images/logo_L.png'
import { useLogin } from './hooks/useLogin'
import { useRegister } from './hooks/useRegister'
import { LoadingOverlay } from '@/components/LoadingOverlay'
import { SuccessModal } from '@/components/SuccessModal'
import LoginForm from './components/LoginForm'
import RegisterForm from './components/RegisterForm'
import { useSchoolAccount } from '@/context/SchoolAccountContext'
import { auth } from '@/config/firebase'
import { DotLoader } from 'react-spinners'
import Footer from '@/components/Footer'

type Mode = 'login' | 'register'

function Login() {
  const navigate = useNavigate()
  const { schoolAccount, isLoading: schoolAccountLoading } = useSchoolAccount()
  // ログインページではbodyとhtmlのスクロールを無効化
  useEffect(() => {
    document.body.classList.add('login-page')
    document.documentElement.classList.add('login-page')
    
    return () => {
      document.body.classList.remove('login-page')
      document.documentElement.classList.remove('login-page')
    }
  }, [])

  useEffect(() => {
    if (schoolAccountLoading) return

    if (auth.currentUser && schoolAccount) {
      navigate('/dashboards', { replace: true })
      return
    }

    if (auth.currentUser && !schoolAccount) {
      navigate('/error/403', { replace: true })
    }
  }, [schoolAccount, schoolAccountLoading, navigate])
  const [mode, setMode] = useState<Mode>('login')
  const [isLoginLoading, setIsLoginLoading] = useState(false)
  
  const { setEmailError, setPasswordError, reset: resetLogin } = useLogin()
  const {
    registerData,
    fieldErrors,
    error: registerError,
    isLoading: isRegisterLoading,
    showSuccessModal,
    isPostalCodeSearching,
    handleRegisterChange,
    handlePostalCodeSearch,
    handleClearFieldError,
    handleSubmit: handleRegisterSubmit,
    reset: resetRegister,
    clearError: clearRegisterError,
    closeSuccessModal,
  } = useRegister()

  const handleClearErrors = () => {
    setEmailError(null)
    setPasswordError(null)
    clearRegisterError()
  }

  const handleRegisterSubmitWithModeSwitch = async (e: React.FormEvent) => {
    const success = await handleRegisterSubmit(e)
    if (success) {
      setMode('login')
    }
  }

  // 認証状態判定中はログイン画面本体を描画しない（ちらつき防止）
  if (schoolAccountLoading) {
    return (
      <div className="loading-overlay">
        <DotLoader color="var(--irogami-primary)" size={40} speedMultiplier={1} />
      </div>
    )
  }

  // 既ログイン時は遷移完了まで何も描画しない（ちらつき防止）
  if (auth.currentUser) {
    return null
  }

  return (
    <div className="auth-page-shell">
      <div className="login-container">
        <LoadingOverlay show={isLoginLoading || isRegisterLoading} />
        <SuccessModal
          show={showSuccessModal}
          message={
            'サポートチームよりお電話で登録内容を確認させていただきます。\n' +
            '確認後、本登録となりアカウントが開設されますので、今しばらくお待ちください。'
          }
          onClose={closeSuccessModal}
        />
        <div className="login-card">
          <img src={logoImage} alt="IROGAMI" className="login-logo" />
          
          {/* タブ切り替え */}
          <div className="auth-tabs">
            <button
              type="button"
              className={`tab-button ${mode === 'login' ? 'active' : ''}`}
              onClick={() => {
                setMode('login')
                handleClearErrors()
                resetRegister()
              }}
            >
              ログイン
            </button>
            <button
              type="button"
              className={`tab-button ${mode === 'register' ? 'active' : ''}`}
              onClick={() => {
                setMode('register')
                handleClearErrors()
                resetLogin()
              }}
            >
              新規登録
            </button>
          </div>

          {mode === 'login' ? (
            <LoginForm onClearErrors={handleClearErrors} onLoadingChange={setIsLoginLoading} />
          ) : (
            <RegisterForm
              registerData={registerData}
              error={registerError}
              fieldErrors={fieldErrors}
              isPostalCodeSearching={isPostalCodeSearching}
              onRegisterChange={handleRegisterChange}
              onPostalCodeSearch={handlePostalCodeSearch}
              onClearFieldError={handleClearFieldError}
              onSubmit={handleRegisterSubmitWithModeSwitch}
            />
          )}

          {/* お問い合わせリンク（運用会社サイト） */}
          <div className="contact-link">
            <a
              href="https://irogami-taiken.com/#section-694"
              className="link-text"
              target="_blank"
              rel="noopener noreferrer"
            >
              運用会社へのお問い合わせ
            </a>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default Login

